import { useTenantsStore } from '@/stores/tenants'
import type { PreferredLanguage } from '@/models/User'

export type RunLanguageName = 'french' | 'english'

/** Fold any locale tag (`fr`, `fr-FR`, `en-US`…) onto the two languages MAC writes in. */
export function languageCodeOf(tag: string): PreferredLanguage {
  return tag.toLowerCase().startsWith('fr') ? 'fr' : 'en'
}

/**
 * The language a run writes its output in (MYAC-1323): the company's chosen
 * language, so every member's run on the same company reads alike. A company
 * that never chose writes in English, never in the language of whoever starts
 * the run: French is still in beta (MYAC-1522), and the backend's scheduled runs
 * already default to English for such a company.
 */
export function runLanguageCode(): PreferredLanguage {
  return companyLanguageCode() ?? 'en'
}

/** The language the company chose, or null when it never did. */
export function companyLanguageCode(): PreferredLanguage | null {
  const locale = useTenantsStore().currentTenant?.locale
  return locale === 'fr' || locale === 'en' ? locale : null
}

/**
 * The Assessment `language` field for runs that never sent one before (standard
 * assessment, technical file, supplier list): set only when the company chose.
 * Absent, the backend keeps its English default — what these runs always wrote in,
 * so a company that never chose sees no change, and a technical-file section
 * rerun stays in the language of the document around it.
 */
export function companyLanguageField(): { language?: RunLanguageName } {
  const code = companyLanguageCode()
  return code === null ? {} : { language: code === 'fr' ? 'french' : 'english' }
}

/** The same language, spelled as the Assessment `language` field (agents' output_language). */
export function runLanguageName(): RunLanguageName {
  return runLanguageCode() === 'fr' ? 'french' : 'english'
}
